import React, { useState, useEffect, useRef } from 'react';
import { User, Settings, CreditCard, Calendar, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';

const PatientAdminPage = () => {
  const [activeTab, setActiveTab] = useState('my-details');
  
  // Individual editing states for each section
  const [editingSection, setEditingSection] = useState(null);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [patientExists, setPatientExists] = useState(false);
  
  // Form data states for each section
  const [generalData, setGeneralData] = useState({
    patientId: '',
    patientName: '',
    dateOfBirth: '',
    age: '',
    gender: '',
    height: '',
    weight: ''
  });

  const [contactData, setContactData] = useState({
    phone: '',
    email: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: 'Australia'
  });

  const [nextOfKinData, setNextOfKinData] = useState({
    name: '',
    relationship: '',
    phone: '',
    email: ''
  });

  const [accountsDetailsData, setAccountsDetailsData] = useState({
    medicareNumber: '',
    medicareExpiryDate: '',
    pensioncardNumber: '',
    pensioncardExpiryDate: '',
    privateHealthCo: '',
    privateHealthcardNumber: '',
    privateHealthcardExpiryDate: ''
  });

  const [accountsDetailsExists, setAccountsDetailsExists] = useState(false);

  // Backup data for cancel functionality
  const [backupData, setBackupData] = useState({});

  // Notification state
  const [notification, setNotification] = useState(null);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
  const [editingPaymentSection, setEditingPaymentSection] = useState(false);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [newPaymentMethod, setNewPaymentMethod] = useState({
    cardType: '',
    nameOnCard: '',
    cardNumber: '',
    cardExpiryMonth: '',
    cardExpiryYear: '',
    isDefault: false
  });
  const [backupPaymentMethods, setBackupPaymentMethods] = useState([]);

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Patient Admin';
  }, []);

  // Fetch user data from localStorage or context
  const getCurrentUser = () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  };

  // Fetch patient data
  const fetchPatientData = async () => {
    setLoading(true);
    try {
      const user = getCurrentUser();
      if (!user) {
        console.error('No user found in localStorage');
        showNotification('User not authenticated', 'error');
        return;
      }

      // Debug: Log token information
      const token = localStorage.getItem('authToken');
      console.log('Token exists:', !!token);
      console.log('Token length:', token ? token.length : 0);
      console.log('User ID:', user.id);

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const patientResponse = await fetch(`${apiBaseUrl}/patients/by-user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Patient response status:', patientResponse.status);
      console.log('Patient response headers:', Object.fromEntries(patientResponse.headers.entries()));

      if (patientResponse.ok) {
        const patientData = await patientResponse.json();
        setPatientExists(true);
        populatePatientData(patientData);
      } else if (patientResponse.status === 404) {
        // Patient record doesn't exist, fetch user data
        setPatientExists(false);
        await fetchUserData(user);
      } else if (patientResponse.status === 401) {
        console.error('401 Unauthorized - Token might be expired or invalid');
        console.error('Response body:', await patientResponse.text());
        showNotification('Authentication failed. Please log in again.', 'error');
        // Redirect to login
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return;
      } else {
        const errorText = await patientResponse.text();
        console.error('Patient API error:', patientResponse.status, errorText);
        throw new Error(`Failed to fetch patient data: ${patientResponse.status} ${errorText}`);
      }

      // Fetch accounts details
      await fetchAccountsDetails(user);
    } catch (error) {
      console.error('Error fetching patient data:', error);
      if (error.message.includes('Failed to fetch')) {
        showNotification('Network error. Please check your connection.', 'error');
      } else {
        showNotification('Failed to load patient data', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch user data when patient record doesn't exist
  const fetchUserData = async (user) => {
    try {
      const token = localStorage.getItem('authToken');
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const userResponse = await fetch(`${apiBaseUrl}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('User response status:', userResponse.status);

      if (userResponse.ok) {
        const userData = await userResponse.json();
        populateUserData(userData);
      } else if (userResponse.status === 401) {
        console.error('401 Unauthorized in fetchUserData');
        showNotification('Authentication failed. Please log in again.', 'error');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return;
      } else {
        const errorText = await userResponse.text();
        console.error('User API error:', userResponse.status, errorText);
        throw new Error(`Failed to fetch user data: ${userResponse.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      if (error.message.includes('Failed to fetch')) {
        showNotification('Network error. Please check your connection.', 'error');
      } else {
        showNotification('Failed to load user data', 'error');
      }
    }
  };

  // Populate data from patient record
  const populatePatientData = (patientData) => {
    setGeneralData({
      patientId: patientData.patient_id || '',
      patientName: `${patientData.first_name || ''} ${patientData.last_name || ''}`.trim(),
      dateOfBirth: patientData.dob ? new Date(patientData.dob).toISOString().split('T')[0] : '',
      age: patientData.age ? patientData.age.toString() : '',
      gender: patientData.gender || '',
      height: patientData.height || '',
      weight: patientData.weight || ''
    });

    setContactData(prevContactData => ({
      phone: patientData.phone || '',
      email: patientData.user_email || prevContactData.email || '', // Preserve existing email if not in response
      address: patientData.address || '',
      suburb: patientData.suburb || '',
      state: patientData.state || '',
      postcode: patientData.postcode || '',
      country: patientData.country || 'Australia'
    }));

    setNextOfKinData({
      name: patientData.nok_name || '',
      relationship: patientData.nok_relation || '',
      phone: patientData.nok_phone || '',
      email: patientData.nok_email || ''
    });
  };

  // Populate data from user record (when patient doesn't exist)
  const populateUserData = (userData) => {
    setGeneralData({
      patientId: 'Not Assigned',
      patientName: `${userData.first_name || ''} ${userData.last_name || ''}`.trim(),
      dateOfBirth: '',
      age: '',
      gender: '',
      height: '',
      weight: ''
    });

    setContactData({
      phone: '',
      email: userData.email || '',
      address: '',
      suburb: '',
      state: '',
      postcode: '',
      country: 'Australia'
    });

    setNextOfKinData({
      name: '',
      relationship: '',
      phone: '',
      email: ''
    });
  };

  // Fetch accounts details
  const fetchAccountsDetails = async (user) => {
    try {
      const token = localStorage.getItem('authToken');
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${apiBaseUrl}/patient-accounts-details/by-user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Accounts response status:', response.status);

      if (response.ok) {
        const accountsData = await response.json();
        console.log('Accounts data received:', accountsData);
        setAccountsDetailsExists(true);
        populateAccountsDetailsData(accountsData);
      } else if (response.status === 404) {
        // Accounts details don't exist, keep empty state
        console.log('No accounts details found for user, keeping empty state');
        setAccountsDetailsExists(false);
        setAccountsDetailsData({
          medicareNumber: '',
          medicareExpiryDate: '',
          pensioncardNumber: '',
          pensioncardExpiryDate: '',
          privateHealthCo: '',
          privateHealthcardNumber: '',
          privateHealthcardExpiryDate: ''
        });
      } else if (response.status === 401) {
        console.error('401 Unauthorized in fetchAccountsDetails');
        showNotification('Authentication failed. Please log in again.', 'error');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return;
      } else {
        const errorText = await response.text();
        console.error('Accounts API error:', response.status, errorText);
        throw new Error(`Failed to fetch accounts details: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error fetching accounts details:', error);
      if (error.message.includes('Failed to fetch')) {
        showNotification('Network error. Please check your connection.', 'error');
      }
      // Don't show error notification for accounts details as they might not exist
    }
  };

  // Populate accounts details data
  const populateAccountsDetailsData = (accountsData) => {
    // Check if age is below 60 or empty, then set pension card fields to empty
    const age = parseInt(generalData.age) || 0;
    const shouldSetPensionCardToEmpty = age < 60 || !generalData.age;
    
    setAccountsDetailsData({
      medicareNumber: accountsData.medicare_number || '',
      medicareExpiryDate: accountsData.medicare_expiry_date ? new Date(accountsData.medicare_expiry_date).toISOString().split('T')[0] : '',
      pensioncardNumber: shouldSetPensionCardToEmpty ? '' : (accountsData.pensioncard_number || ''),
      pensioncardExpiryDate: shouldSetPensionCardToEmpty ? '' : (accountsData.pensioncard_expiry_date ? new Date(accountsData.pensioncard_expiry_date).toISOString().split('T')[0] : ''),
      privateHealthCo: accountsData.private_health_co || '',
      privateHealthcardNumber: accountsData.private_healthcard_number || '',
      privateHealthcardExpiryDate: accountsData.private_healthcard_expiry_date ? new Date(accountsData.private_healthcard_expiry_date).toISOString().split('T')[0] : ''
    });
  };

  // Create patient record for first time
  const createPatientRecord = async (data) => {
    try {
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not found');
      }

      const patientData = {
        user_id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        dob: data.dateOfBirth || null,
        gender: data.gender || null,
        height: data.height || null,
        weight: data.weight || null,
        phone: data.phone || null,
        address: data.address || null,
        suburb: data.suburb || null,
        state: data.state || null,
        postcode: data.postcode || null,
        country: data.country || 'Australia',
        nok_name: data.nok_name || null,
        nok_relation: data.nok_relation || null,
        nok_phone: data.nok_phone || null,
        nok_email: data.nok_email || null
      };

        const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
        const response = await fetch(`${apiBaseUrl}/patients/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(patientData)
      });

      if (response.ok) {
        const newPatient = await response.json();
        setPatientExists(true);
        populatePatientData(newPatient);
        return true;
      } else {
        throw new Error('Failed to create patient record');
      }
    } catch (error) {
      console.error('Error creating patient record:', error);
      throw error;
    }
  };

  // Update patient record
  const updatePatientRecord = async (data) => {
    try {
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not found');
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const patientResponse = await fetch(`${apiBaseUrl}/patients/by-user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (patientResponse.ok) {
        const patientData = await patientResponse.json();
        
        // Prepare update data with only the fields that the backend expects
        const updateData = {
          first_name: data.firstName || patientData.first_name,
          last_name: data.lastName || patientData.last_name,
          dob: data.dateOfBirth || patientData.dob || null,
          gender: data.gender || patientData.gender,
          height: data.height || patientData.height,
          weight: data.weight || patientData.weight,
          phone: data.phone || patientData.phone,
          address: data.address || patientData.address,
          suburb: data.suburb || patientData.suburb,
          state: data.state || patientData.state,
          postcode: data.postcode || patientData.postcode,
          country: data.country || patientData.country || "Australia",
          nok_name: data.nokName || patientData.nok_name,
          nok_relation: data.nokRelation || patientData.nok_relation,
          nok_phone: data.nokPhone || patientData.nok_phone,
          nok_email: data.nokEmail || patientData.nok_email,
          is_active: data.isActive !== undefined ? data.isActive : patientData.is_active
        };

        console.log('Sending patient update data:', updateData);
        
        const response = await fetch(`${apiBaseUrl}/patients/${patientData.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        console.log('Patient update response status:', response.status);
        
        if (response.ok) {
          const updatedPatient = await response.json();
          // Preserve the email field when updating patient data
          updatedPatient.user_email = contactData.email;
          // Preserve general details if they exist in current state
          if (generalData.dateOfBirth || generalData.age || generalData.gender || generalData.height || generalData.weight) {
            updatedPatient.dob = generalData.dateOfBirth ? new Date(generalData.dateOfBirth).toISOString().split('T')[0] : updatedPatient.dob;
            updatedPatient.age = generalData.age ? parseInt(generalData.age) : updatedPatient.age;
            updatedPatient.gender = generalData.gender || updatedPatient.gender;
            updatedPatient.height = generalData.height || updatedPatient.height;
            updatedPatient.weight = generalData.weight || updatedPatient.weight;
          }
          populatePatientData(updatedPatient);
          return true;
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Patient update failed:', response.status, errorData);
          throw new Error(`Failed to update patient record: ${errorData.detail || 'Unknown error'}`);
        }
      } else {
        throw new Error('Patient record not found');
      }
    } catch (error) {
      console.error('Error updating patient record:', error);
      throw error;
    }
  };

  // Create accounts details record
  const createAccountsDetailsRecord = async (data) => {
    try {
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not found');
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const patientResponse = await fetch(`${apiBaseUrl}/patients/by-user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (patientResponse.ok) {
        const patientData = await patientResponse.json();
        
        // Check if age is below 60 or empty, then set pension card fields to null
        const age = parseInt(generalData.age) || 0;
        const shouldSetPensionCardToNull = age < 60 || !generalData.age;
        
        const accountsData = {
          user_id: user.id,
          patient_id: patientData.id,
          medicare_number: data.medicareNumber || null,
          medicare_expiry_date: data.medicareExpiryDate || null,
          pensioncard_number: shouldSetPensionCardToNull ? null : (data.pensioncardNumber || null),
          pensioncard_expiry_date: shouldSetPensionCardToNull ? null : (data.pensioncardExpiryDate || null),
          private_health_co: data.privateHealthCo || null,
          private_healthcard_number: data.privateHealthcardNumber || null,
          private_healthcard_expiry_date: data.privateHealthcardExpiryDate || null
        };

        const response = await fetch(`${apiBaseUrl}/patient-accounts-details/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(accountsData)
        });

        if (response.ok) {
          const createdAccounts = await response.json();
          setAccountsDetailsExists(true);
          populateAccountsDetailsData(createdAccounts);
          return true;
        } else {
          throw new Error('Failed to create accounts details record');
        }
      } else {
        throw new Error('Patient record not found');
      }
    } catch (error) {
      console.error('Error creating accounts details record:', error);
      throw error;
    }
  };

  // Update accounts details record
  const updateAccountsDetailsRecord = async (data) => {
    try {
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not found');
      }

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const accountsResponse = await fetch(`${apiBaseUrl}/patient-accounts-details/by-user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (accountsResponse.ok) {
        const accountsData = await accountsResponse.json();
        
        // Check if age is below 60 or empty, then set pension card fields to null
        const age = parseInt(generalData.age) || 0;
        const shouldSetPensionCardToNull = age < 60 || !generalData.age;
        
        const updateData = {
          medicare_number: data.medicareNumber || null,
          medicare_expiry_date: data.medicareExpiryDate || null,
          pensioncard_number: shouldSetPensionCardToNull ? null : (data.pensioncardNumber || null),
          pensioncard_expiry_date: shouldSetPensionCardToNull ? null : (data.pensioncardExpiryDate || null),
          private_health_co: data.privateHealthCo || null,
          private_healthcard_number: data.privateHealthcardNumber || null,
          private_healthcard_expiry_date: data.privateHealthcardExpiryDate || null
        };

        const response = await fetch(`${apiBaseUrl}/patient-accounts-details/${accountsData.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        if (response.ok) {
          const updatedAccounts = await response.json();
          populateAccountsDetailsData(updatedAccounts);
          return true;
        } else {
          throw new Error('Failed to update accounts details record');
        }
      } else {
        throw new Error('Accounts details record not found');
      }
    } catch (error) {
      console.error('Error updating accounts details record:', error);
      throw error;
    }
  };

  // Show notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Handle edit button click
  const handleEditRecord = (section) => {
    setEditingSection(section);
    
    // Backup current data for cancel functionality
    let currentData = {};
    switch (section) {
      case 'general':
        currentData = { ...generalData };
        break;
      case 'contact':
        currentData = { ...contactData };
        break;
      case 'nextOfKin':
        currentData = { ...nextOfKinData };
        break;
      case 'accountsDetails':
        currentData = { ...accountsDetailsData };
        break;
      default:
        break;
    }
    setBackupData(currentData);
  };

  // Handle save changes
  const handleSaveChanges = async (section) => {
    try {
      setEditingSection(null);
      
      let sectionName = '';
      let updateData = {};
      
      switch (section) {
        case 'general':
          sectionName = 'General Details';
          updateData = {
            dateOfBirth: generalData.dateOfBirth,
            age: generalData.age,
            gender: generalData.gender,
            height: generalData.height,
            weight: generalData.weight
          };
          break;
        case 'contact':
          sectionName = 'Contact Details';
          updateData = {
            phone: contactData.phone,
            email: contactData.email,
            address: contactData.address,
            suburb: contactData.suburb,
            state: contactData.state,
            postcode: contactData.postcode,
            country: contactData.country
          };
          break;
        case 'nextOfKin':
          sectionName = 'Next of Kin Details';
          updateData = {
            nok_name: nextOfKinData.name,
            nok_relation: nextOfKinData.relationship,
            nok_phone: nextOfKinData.phone,
            nok_email: nextOfKinData.email
          };
          break;
        case 'accountsDetails':
          sectionName = 'Account Details';
          updateData = {
            medicareNumber: accountsDetailsData.medicareNumber,
            medicareExpiryDate: accountsDetailsData.medicareExpiryDate,
            pensioncardNumber: accountsDetailsData.pensioncardNumber,
            pensioncardExpiryDate: accountsDetailsData.pensioncardExpiryDate,
            privateHealthCo: accountsDetailsData.privateHealthCo,
            privateHealthcardNumber: accountsDetailsData.privateHealthcardNumber,
            privateHealthcardExpiryDate: accountsDetailsData.privateHealthcardExpiryDate
          };
          break;
        default:
          break;
      }

      if (section === 'accountsDetails') {
        if (accountsDetailsExists) {
          // Update existing accounts details record
          await updateAccountsDetailsRecord(updateData);
        } else {
          // Create new accounts details record for first time
          await createAccountsDetailsRecord(updateData);
        }
      } else if (patientExists) {
        // Update existing patient record
        await updatePatientRecord(updateData);
      } else {
        // Create new patient record for first time
        await createPatientRecord(updateData);
      }
      
      showNotification(`${sectionName} have been updated successfully!`);
    } catch (error) {
      console.error('Error saving changes:', error);
      showNotification('Failed to save changes. Please try again.', 'error');
      // Restore backup data on error
      handleCancelChanges(section);
    }
  };

  // Handle cancel changes
  const handleCancelChanges = (section) => {
    setEditingSection(null);
    
    // Restore backup data
    switch (section) {
      case 'general':
        setGeneralData(backupData);
        break;
      case 'contact':
        setContactData(backupData);
        break;
      case 'nextOfKin':
        setNextOfKinData(backupData);
        break;
      case 'accountsDetails':
        setAccountsDetailsData(backupData);
        break;
      default:
        break;
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age.toString();
  };

  // Handle input change
  const handleInputChange = (section, field, value) => {
    switch (section) {
      case 'general':
        setGeneralData(prev => {
          const updated = { ...prev, [field]: value };
          // Auto-calculate age when date of birth changes
          if (field === 'dateOfBirth') {
            updated.age = calculateAge(value);
          }
          return updated;
        });
        break;
      case 'contact':
        setContactData(prev => ({ ...prev, [field]: value }));
        break;
      case 'nextOfKin':
        setNextOfKinData(prev => ({ ...prev, [field]: value }));
        break;
      case 'accountsDetails':
        setAccountsDetailsData(prev => ({ ...prev, [field]: value }));
        break;
      default:
        break;
    }
  };

  // State for gender dropdown
  const [genderDropdownOpen, setGenderDropdownOpen] = useState(false);
  const genderDropdownRef = useRef(null);

  // Close gender dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (genderDropdownRef.current && !genderDropdownRef.current.contains(event.target)) {
        setGenderDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle gender selection
  const handleGenderSelect = (gender) => {
    handleInputChange('general', 'gender', gender);
    setGenderDropdownOpen(false);
  };

  // Render gender dropdown
  const renderGenderDropdown = (label, value, field, section, placeholder) => {
    const isEditing = editingSection === section;
    const genderOptions = ['Male', 'Female', 'Other'];
    
    return (
      <div className="mb-3" ref={genderDropdownRef}>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <div className="relative">
          <div
            onClick={() => isEditing && setGenderDropdownOpen(!genderDropdownOpen)}
            className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer flex items-center justify-between ${
              isEditing ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
            }`}
          >
            <span className={value ? 'text-gray-900' : 'text-gray-500'}>
              {value || placeholder}
            </span>
            {isEditing && (
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                  genderDropdownOpen ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </div>
          
          {isEditing && genderDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
              {genderOptions.map((option) => (
                <div
                  key={option}
                  onClick={() => handleGenderSelect(option)}
                  className={`px-3 py-2 cursor-pointer hover:bg-gray-100 first:rounded-t-md last:rounded-b-md ${
                    value === option ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                  }`}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Render form field with edit functionality
  const renderFormField = (label, value, field, section, placeholder, type = 'text', readOnly = false) => {
    const isEditing = editingSection === section && !readOnly;
    
    // Check if this is a pension card field and if age is below 60 or empty
    const isPensionCardField = field === 'pensioncardNumber' || field === 'pensioncardExpiryDate';
    const age = parseInt(generalData.age) || 0;
    const shouldShowNA = isPensionCardField && (age < 60 || !generalData.age);
    
    if (shouldShowNA) {
      return (
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {label}
          </label>
          <input
            type="text"
            value="NA"
            placeholder="NA"
            readOnly={true}
            className="w-full px-3 py-1 border border-gray-300 rounded-md bg-gray-100 text-gray-500"
          />
        </div>
      );
    }
    
    return (
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
        <input
          type={type}
          value={value}
          placeholder={placeholder}
          readOnly={!isEditing}
          onChange={(e) => handleInputChange(section, field, e.target.value)}
          className={`w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            isEditing ? 'bg-white' : 'bg-gray-50'
          }`}
        />
      </div>
    );
  };

  // Payment methods API functions
  const fetchPaymentMethods = async () => {
    try {
      setPaymentMethodsLoading(true);
      const user = getCurrentUser();
      if (!user) return;

      const token = localStorage.getItem('authToken');
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

      // First get patient data to get patient_id
      const patientResponse = await fetch(`${apiBaseUrl}/patients/by-user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Patient response for payment methods:', patientResponse.status);

      if (patientResponse.ok) {
        const patientData = await patientResponse.json();
        
        // Fetch payment methods for this patient
        const response = await fetch(`${apiBaseUrl}/patient-payment-details/by-patient/${patientData.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('Payment methods response:', response.status);

        if (response.ok) {
          const paymentMethodsData = await response.json();
          setPaymentMethods(paymentMethodsData);
        } else if (response.status === 404) {
          // No payment methods found, set empty array
          setPaymentMethods([]);
        } else if (response.status === 401) {
          console.error('401 Unauthorized in fetchPaymentMethods');
          showNotification('Authentication failed. Please log in again.', 'error');
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          window.location.href = '/auth/login';
          return;
        } else {
          const errorText = await response.text();
          console.error('Payment methods API error:', response.status, errorText);
          throw new Error(`Failed to fetch payment methods: ${response.status} ${errorText}`);
        }
      } else if (patientResponse.status === 401) {
        console.error('401 Unauthorized in fetchPaymentMethods (patient fetch)');
        showNotification('Authentication failed. Please log in again.', 'error');
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/login';
        return;
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      if (error.message.includes('Failed to fetch')) {
        showNotification('Network error. Please check your connection.', 'error');
      } else {
        showNotification('Failed to load payment methods', 'error');
      }
    } finally {
      setPaymentMethodsLoading(false);
    }
  };

  const createPaymentMethod = async (paymentData) => {
    try {
      const user = getCurrentUser();
      if (!user) return false;

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      // Get patient data to get patient_id
      const patientResponse = await fetch(`${apiBaseUrl}/patients/by-user/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (patientResponse.ok) {
        const patientData = await patientResponse.json();
        
        // Extract last 4 digits from card number
        const cardLastFour = paymentData.cardNumber.slice(-4);
        
        const paymentMethodData = {
          user_id: user.id,
          patient_id: patientData.id,
          is_default: paymentData.isDefault,
          card_type: paymentData.cardType,
          name_on_card: paymentData.nameOnCard,
          card_number: paymentData.cardNumber, // In production, this should be encrypted
          card_expiry_month: paymentData.cardExpiryMonth,
          card_expiry_year: paymentData.cardExpiryYear,
          card_last_four: cardLastFour
        };

        console.log('Creating payment method with data:', paymentMethodData);

        const response = await fetch(`${apiBaseUrl}/patient-payment-details/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(paymentMethodData)
        });

        console.log('Create payment method response status:', response.status);

        if (response.ok) {
          const result = await response.json();
          console.log('Payment method created successfully:', result);
          await fetchPaymentMethods(); // Refresh the list
          return true;
        } else {
          const errorText = await response.text();
          console.error('Create payment method error response:', errorText);
          throw new Error(`Failed to create payment method: ${response.status} ${errorText}`);
        }
      }
      return false;
    } catch (error) {
      console.error('Error creating payment method:', error);
      showNotification(`Failed to create payment method: ${error.message}`, 'error');
      return false;
    }
  };

  const updatePaymentMethod = async (paymentMethodId, paymentData) => {
    try {
      const updateData = {
        card_type: paymentData.cardType,
        name_on_card: paymentData.nameOnCard,
        card_number: paymentData.cardNumber, // In production, this should be encrypted
        card_expiry_month: paymentData.cardExpiryMonth,
        card_expiry_year: paymentData.cardExpiryYear,
        card_last_four: paymentData.cardNumber.slice(-4)
      };

      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${apiBaseUrl}/patient-payment-details/${paymentMethodId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        await fetchPaymentMethods(); // Refresh the list
        return true;
      } else {
        throw new Error('Failed to update payment method');
      }
    } catch (error) {
      console.error('Error updating payment method:', error);
      showNotification('Failed to update payment method', 'error');
      return false;
    }
  };

  const deletePaymentMethod = async (paymentMethodId) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${apiBaseUrl}/patient-payment-details/${paymentMethodId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        await fetchPaymentMethods(); // Refresh the list
        return true;
      } else {
        throw new Error('Failed to delete payment method');
      }
    } catch (error) {
      console.error('Error deleting payment method:', error);
      showNotification('Failed to delete payment method', 'error');
      return false;
    }
  };

  const setDefaultPaymentMethod = async (paymentMethodId) => {
    try {
      console.log('Setting payment method as default:', paymentMethodId);
      
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await fetch(`${apiBaseUrl}/patient-payment-details/${paymentMethodId}/set-default`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      console.log('Set default payment method response status:', response.status);

      if (response.ok) {
        const result = await response.json();
        console.log('Payment method set as default successfully:', result);
        await fetchPaymentMethods(); // Refresh the list
        return true;
      } else {
        const errorText = await response.text();
        console.error('Set default payment method error response:', errorText);
        throw new Error(`Failed to set default payment method: ${response.status} ${errorText}`);
      }
    } catch (error) {
      console.error('Error setting default payment method:', error);
      showNotification(`Failed to set default payment method: ${error.message}`, 'error');
      return false;
    }
  };

  // Payment methods UI handlers
  const handleEditPaymentSection = () => {
    setEditingPaymentSection(true);
    setBackupPaymentMethods([...paymentMethods]);
    // Add a new empty row for adding payment method
    const newRow = {
      id: `temp-${Date.now()}`,
      is_temp: true,
      card_type: '',
      name_on_card: '',
      card_number: '',
      card_expiry_month: '',
      card_expiry_year: '',
      card_last_four: '',
      is_default: false
    };
    setPaymentMethods([...paymentMethods, newRow]);
  };

  const handleCancelPaymentChanges = () => {
    setEditingPaymentSection(false);
    setPaymentMethods(backupPaymentMethods);
    setEditingPaymentMethod(null);
  };

  const handleSavePaymentChanges = async () => {
    try {
      setEditingPaymentSection(false);
      
      // Find the temporary row (new payment method)
      const tempRow = paymentMethods.find(method => method.is_temp);
      
      if (tempRow) {
        // Extract the data from the temporary row
        const paymentData = {
          cardType: tempRow.card_type,
          nameOnCard: tempRow.name_on_card,
          cardNumber: tempRow.card_number,
          cardExpiryMonth: tempRow.card_expiry_month,
          cardExpiryYear: tempRow.card_expiry_year,
          isDefault: tempRow.is_default
        };

        // Validate required fields
        if (!paymentData.cardType || !paymentData.nameOnCard || 
            !paymentData.cardNumber || !paymentData.cardExpiryMonth || 
            !paymentData.cardExpiryYear) {
          showNotification('Please fill in all required fields', 'error');
          return;
        }

        // Create the payment method
        const success = await createPaymentMethod(paymentData);
        if (success) {
          showNotification('Payment method added successfully!', 'success');
        }
      } else {
        // No new payment method to add, just refresh the list
        await fetchPaymentMethods();
        showNotification('Payment details updated successfully!', 'success');
      }
    } catch (error) {
      console.error('Error saving payment changes:', error);
      showNotification('Failed to save payment changes', 'error');
    }
  };

  const handlePaymentMethodInputChange = (methodId, field, value) => {
    setPaymentMethods(prev => 
      prev.map(method => 
        method.id === methodId 
          ? { ...method, [field]: value }
          : method
      )
    );
  };

  const handleDeletePaymentMethod = async (paymentMethodId) => {
    // Find the payment method to show in confirmation
    const paymentMethod = paymentMethods.find(method => method.id === paymentMethodId);
    
    const confirmMessage = `Are you sure you want to delete this payment method?\n\nCard Type: ${paymentMethod.card_type}\nName: ${paymentMethod.name_on_card}\nCard Number: ************${paymentMethod.card_last_four}\nExpiry: ${paymentMethod.card_expiry_month}/${paymentMethod.card_expiry_year}`;
    
    if (window.confirm(confirmMessage)) {
      const success = await deletePaymentMethod(paymentMethodId);
      if (success) {
        showNotification('Payment details have been updated successfully!', 'success');
      }
    }
  };

  const handleDefaultCheckboxChange = async (paymentMethodId) => {
    const success = await setDefaultPaymentMethod(paymentMethodId);
    if (success) {
      showNotification('Default payment method updated!', 'success');
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchPatientData();
  }, []);

  // Recalculate accounts details when age changes
  useEffect(() => {
    if (accountsDetailsExists) {
      // Re-fetch accounts details to update pension card fields based on new age
      const user = getCurrentUser();
      if (user) {
        fetchAccountsDetails(user);
      }
    }
  }, [generalData.age]);

  // Fetch payment methods when patient data is loaded
  useEffect(() => {
    if (patientExists) {
      fetchPaymentMethods();
    }
  }, [patientExists]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading patient data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg ${
          notification.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {notification.message}
        </div>
      )}

      <div className="flex">
        {/* Left Sidebar - 20% */}
        <div className="w-1/5 bg-gray-100 min-h-screen p-4">
          <div className="space-y-2">
            <button
              onClick={() => setActiveTab('my-details')}
              className={`w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] ${
                activeTab === 'my-details'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300 hover:shadow-sm'
              }`}
            >
              My Details
            </button>
            <Link
              to="/patient/admin/preferences"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              Preferences
            </Link>
            <Link
              to="/patient/admin/accounts"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              Accounts
            </Link>
            <Link
              to="/patient/admin/bookings"
              className="w-full text-left px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 bg-gray-200 text-gray-700 hover:bg-gray-300 h-12 flex items-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transform hover:scale-[1.02] active:scale-[0.98] hover:shadow-sm"
            >
              Bookings
            </Link>
          </div>
        </div>

        {/* Right Content Area - 80% */}
        <div className="w-4/5 bg-gray-100 p-4">
          <div className="w-full">
            
            {/* General Details Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h3 className="font-semibold text-sm">General Details</h3>
              </div>
              <div className="p-4">
                <div className="flex gap-3">
                  <div className="w-[15%]">
                    {renderFormField('Patient ID', generalData.patientId, 'patientId', 'general', 'Patient ID', 'text', true)}
                  </div>
                  <div className="w-[25%]">
                    {renderFormField('Patient Name', generalData.patientName, 'patientName', 'general', 'Patient Name', 'text', true)}
                  </div>
                  <div className="w-[15%]">
                    {renderFormField('Date Of Birth', generalData.dateOfBirth, 'dateOfBirth', 'general', 'Patient DOB', 'date')}
                  </div>
                  <div className="w-[10%]">
                    {renderFormField('Age', generalData.age, 'age', 'general', 'Patient Age')}
                  </div>
                  <div className="w-[15%]">
                    {renderGenderDropdown('Gender', generalData.gender, 'gender', 'general', 'Select Gender')}
                  </div>
                  <div className="w-[10%]">
                    {renderFormField('Height', generalData.height, 'height', 'general', 'Height')}
                  </div>
                  <div className="w-[10%]">
                    {renderFormField('Weight', generalData.weight, 'weight', 'general', 'Weight')}
                  </div>
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                  {editingSection !== 'general' ? (
                    <button
                      onClick={() => handleEditRecord('general')}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors"
                    >
                      Edit Record
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleCancelChanges('general')}
                        className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600 transition-colors flex items-center space-x-1"
                      >
                        <X size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={() => handleSaveChanges('general')}
                        className="bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-600 transition-colors flex items-center space-x-1"
                      >
                        <Check size={16} />
                        <span>Save Changes</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Details Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h3 className="font-semibold text-sm">Contact Details</h3>
              </div>
              <div className="p-4">
                {/* First Row: Phone and Email */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    {renderFormField('Phone', contactData.phone, 'phone', 'contact', 'Patient Phone')}
                  </div>
                  <div>
                    {renderFormField('Email', contactData.email, 'email', 'contact', 'Patient Email', 'email')}
                  </div>
                </div>
                {/* Second Row: Address, Suburb, State, Postcode, Country */}
                <div className="flex gap-3">
                  <div className="w-[35%]">
                    {renderFormField('Address', contactData.address, 'address', 'contact', 'Patient Address')}
                  </div>
                  <div className="w-[20%]">
                    {renderFormField('Suburb', contactData.suburb, 'suburb', 'contact', 'Patient Suburb')}
                  </div>
                  <div className="w-[15%]">
                    {renderFormField('State', contactData.state, 'state', 'contact', 'Patient State')}
                  </div>
                  <div className="w-[10%]">
                    {renderFormField('Postcode', contactData.postcode, 'postcode', 'contact', 'Patient Postcode')}
                  </div>
                  <div className="w-[20%]">
                    {renderFormField('Country', contactData.country, 'country', 'contact', 'Patient Country')}
                  </div>
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                  {editingSection !== 'contact' ? (
                    <button
                      onClick={() => handleEditRecord('contact')}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors"
                    >
                      Edit Record
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleCancelChanges('contact')}
                        className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600 transition-colors flex items-center space-x-1"
                      >
                        <X size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={() => handleSaveChanges('contact')}
                        className="bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-600 transition-colors flex items-center space-x-1"
                      >
                        <Check size={16} />
                        <span>Save Changes</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Next Of Kin Details Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h3 className="font-semibold text-sm">Next Of Kin Details</h3>
              </div>
              <div className="p-4">
                <div className="flex gap-3">
                  <div className="w-[30%]">
                    {renderFormField('Next Of Kin Name', nextOfKinData.name, 'name', 'nextOfKin', 'Patient Next Of Kin Name')}
                  </div>
                  <div className="w-[20%]">
                    {renderFormField('Next Of Kin Relationship', nextOfKinData.relationship, 'relationship', 'nextOfKin', 'Patient Next Of Kin Relationship')}
                  </div>
                  <div className="w-[20%]">
                    {renderFormField('Next Of Kin Phone', nextOfKinData.phone, 'phone', 'nextOfKin', 'Patient Next Of Kin Phone')}
                  </div>
                  <div className="w-[30%]">
                    {renderFormField('Next Of Kin Email', nextOfKinData.email, 'email', 'nextOfKin', 'Patient Next Of Kin Email', 'email')}
                  </div>
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                  {editingSection !== 'nextOfKin' ? (
                    <button
                      onClick={() => handleEditRecord('nextOfKin')}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors"
                    >
                      Edit Record
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleCancelChanges('nextOfKin')}
                        className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600 transition-colors flex items-center space-x-1"
                      >
                        <X size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={() => handleSaveChanges('nextOfKin')}
                        className="bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-600 transition-colors flex items-center space-x-1"
                      >
                        <Check size={16} />
                        <span>Save Changes</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Accounts Details Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h3 className="font-semibold text-sm">Accounts Details</h3>
              </div>
              <div className="p-4">
                <div className="flex gap-3">
                  <div className="w-[16%]">
                    {renderFormField('Medicare Number', accountsDetailsData.medicareNumber, 'medicareNumber', 'accountsDetails', 'Enter Medicare Number')}
                  </div>
                  <div className="w-[12%]">
                    {renderFormField('Expiry Date', accountsDetailsData.medicareExpiryDate, 'medicareExpiryDate', 'accountsDetails', 'Medicare Expiry', 'date')}
                  </div>
                  <div className="w-[16%]">
                    {renderFormField('Pension Card Number', accountsDetailsData.pensioncardNumber, 'pensioncardNumber', 'accountsDetails', 'Enter Pension Card Number')}
                  </div>
                  <div className="w-[12%]">
                    {renderFormField('Expiry Date', accountsDetailsData.pensioncardExpiryDate, 'pensioncardExpiryDate', 'accountsDetails', 'Pension Expiry', 'date')}
                  </div>
                  <div className="w-[15%]">
                    {renderFormField('Private Health Co', accountsDetailsData.privateHealthCo, 'privateHealthCo', 'accountsDetails', 'Enter Health Insurance Company')}
                  </div>
                  <div className="w-[17%]">
                    {renderFormField('Private Health Card No.', accountsDetailsData.privateHealthcardNumber, 'privateHealthcardNumber', 'accountsDetails', 'Enter Private Health Card Number')}
                  </div>
                  <div className="w-[12%]">
                    {renderFormField('Expiry Date', accountsDetailsData.privateHealthcardExpiryDate, 'privateHealthcardExpiryDate', 'accountsDetails', 'Private Health Expiry', 'date')}
                  </div>
                </div>
                <div className="flex justify-end mt-4 space-x-2">
                  {editingSection !== 'accountsDetails' ? (
                    <button
                      onClick={() => handleEditRecord('accountsDetails')}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors"
                    >
                      Edit Record
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleCancelChanges('accountsDetails')}
                        className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600 transition-colors flex items-center space-x-1"
                      >
                        <X size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={() => handleSaveChanges('accountsDetails')}
                        className="bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-600 transition-colors flex items-center space-x-1"
                      >
                        <Check size={16} />
                        <span>Save Changes</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Details Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
              <div className="bg-blue-600 text-white px-4 py-2 rounded-t-lg">
                <h3 className="font-semibold text-sm">Payment Details</h3>
              </div>
              <div className="p-4">
                {paymentMethodsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading payment methods...</span>
                  </div>
                ) : paymentMethods.length === 0 && !editingPaymentSection ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No payment methods found.</p>
                    <p className="text-sm">Add your first payment method to get started.</p>
                  </div>
                ) : (
                  <>
                    {paymentMethods.map((method, index) => (
                      <div key={method.id} className="flex gap-3 mb-3">
                        <div className="w-[7%]">
                          {index === 0 && (
                            <label className="block text-sm font-medium text-gray-700 mb-1 text-center">
                              Default
                            </label>
                          )}
                          <div className="flex items-start justify-center pt-1">
                            <input
                              type="checkbox"
                              checked={method.is_default}
                              onChange={() => handleDefaultCheckboxChange(method.id)}
                              disabled={!editingPaymentSection}
                              className={`h-5 w-4 focus:ring-blue-500 border-2 rounded cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                                method.is_default 
                                  ? 'bg-blue-600 border-blue-600 text-white checked:bg-blue-600 checked:border-blue-600' 
                                  : 'bg-white border-gray-300 text-gray-400'
                              }`}
                              style={{
                                accentColor: '#2563eb',
                                transform: method.is_default ? 'scale(1.1)' : 'scale(1)'
                              }}
                            />
                          </div>
                        </div>
                        <div className="w-[15%]">
                          {index === 0 && (
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Card Type
                            </label>
                          )}
                          {editingPaymentSection && method.is_temp ? (
                            <select
                              value={method.card_type}
                              onChange={(e) => handlePaymentMethodInputChange(method.id, 'card_type', e.target.value)}
                              className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            >
                              <option value="">Select Card Type</option>
                              <option value="Visa">Visa</option>
                              <option value="Mastercard">Mastercard</option>
                              <option value="American Express">American Express</option>
                              <option value="Discover">Discover</option>
                            </select>
                          ) : (
                            <input
                              type="text"
                              value={method.card_type}
                              placeholder="Type Of Card"
                              readOnly={true}
                              className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                            />
                          )}
                        </div>
                        <div className="w-[25%]">
                          {index === 0 && (
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Name On Card
                            </label>
                          )}
                          {editingPaymentSection && method.is_temp ? (
                            <input
                              type="text"
                              value={method.name_on_card}
                              onChange={(e) => handlePaymentMethodInputChange(method.id, 'name_on_card', e.target.value)}
                              placeholder="Name as it appears on card"
                              className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            />
                          ) : (
                            <input
                              type="text"
                              value={method.name_on_card}
                              placeholder="Name On Credit Card"
                              readOnly={true}
                              className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                            />
                          )}
                        </div>
                        <div className="w-[20%]">
                          {index === 0 && (
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Card Number
                            </label>
                          )}
                          {editingPaymentSection && method.is_temp ? (
                            <input
                              type="text"
                              value={method.card_number}
                              onChange={(e) => handlePaymentMethodInputChange(method.id, 'card_number', e.target.value)}
                              placeholder="1234 5678 9012 3456"
                              maxLength="19"
                              className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            />
                          ) : (
                            <input
                              type="text"
                              value={`************${method.card_last_four}`}
                              placeholder="Card Number"
                              readOnly={true}
                              className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                            />
                          )}
                        </div>
                        <div className="w-[15%]">
                          {index === 0 && (
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Card Expiry
                            </label>
                          )}
                          {editingPaymentSection && method.is_temp ? (
                            <div className="flex gap-1">
                              <select
                                value={method.card_expiry_month}
                                onChange={(e) => handlePaymentMethodInputChange(method.id, 'card_expiry_month', e.target.value)}
                                className="w-1/2 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                              >
                                <option value="">MM</option>
                                {Array.from({ length: 12 }, (_, i) => {
                                  const month = String(i + 1).padStart(2, '0');
                                  return (
                                    <option key={month} value={month}>
                                      {month}
                                    </option>
                                  );
                                })}
                              </select>
                              <select
                                value={method.card_expiry_year}
                                onChange={(e) => handlePaymentMethodInputChange(method.id, 'card_expiry_year', e.target.value)}
                                className="w-1/2 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-sm"
                              >
                                <option value="">YY</option>
                                {Array.from({ length: 10 }, (_, i) => {
                                  const year = new Date().getFullYear() + i;
                                  return (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          ) : (
                            <input
                              type="text"
                              value={`${method.card_expiry_month}/${method.card_expiry_year}`}
                              placeholder="Card Expiry Date"
                              readOnly={true}
                              className="w-full px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                            />
                          )}
                        </div>
                        <div className="w-[18%] flex items-end gap-2">
                          {index === 0 && (
                            <div className="h-6"></div>
                          )}
                          {!method.is_temp && (
                            <button
                              onClick={() => handleDeletePaymentMethod(method.id)}
                              className="text-red-600 hover:text-red-800 transition-colors duration-200 p-1"
                              title="Delete Payment Method"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
                <div className="flex justify-end mt-4 space-x-2">
                  {!editingPaymentSection ? (
                    <button
                      onClick={handleEditPaymentSection}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors"
                    >
                      Add Payment Method
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={handleCancelPaymentChanges}
                        className="bg-red-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600 transition-colors flex items-center space-x-1"
                      >
                        <X size={16} />
                        <span>Cancel</span>
                      </button>
                      <button
                        onClick={handleSavePaymentChanges}
                        className="bg-green-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-green-600 transition-colors flex items-center space-x-1"
                      >
                        <Check size={16} />
                        <span>Save Changes</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientAdminPage; 